import { navigate } from "gatsby";
import upperFirst from "lodash.upperfirst";
import React, { ReactNode, useCallback } from "react";
import ContentFooter from "../components/ContentFooter/ContentFooter";
import DownloadLink from "../components/DownloadLink/DownloadLink";
import EditorEmbed from "../components/EditorEmbed/EditorEmbed";
import PdfEmbed from "../components/PdfEmbed/PdfEmbed";
import WithSpacing from "../components/Spacing/Spacing";
import StepperBarInternal from "../components/Stepper/StepperBar/StepperBarInternal";
import Text, { TextVariant } from "../components/Text/Text";
import { FormattedMessage } from "react-intl";
import { MicrobitCode, SanitySkill } from "../model/common";
import {
  DownloadCategory,
  SanityIndividualLesson,
  SanityLesson,
  SanityUnitOfWork
} from "../model/lessons";
import { urlForDocument } from "../urls";
import { downloadUrl } from "./cdn";
import { completedCodeKey } from "./defaults";
import { findLessonNumber, lessonDownloadName } from "./downloads";
import BlockContent from "../components/BlockContent/BlockContent";

export const AdditionalSkills = ({
  additionalSkills,
  className
}: {
  additionalSkills: SanitySkill[];
  className: string;
}) => (
  <>
    {additionalSkills && !!additionalSkills.length && (
      <>
        <Text variant="h3">
          <FormattedMessage id="additional-skills" />
        </Text>
        <Text className={className} variant="defaultLight" gutterBottom={true}>
          {additionalSkills.map((skill, index) => (
            <span key={skill.name}>
              {index === 0 ? upperFirst(skill.name) : skill.name}
              {index < additionalSkills.length - 1 && ", "}
            </span>
          ))}
          {"."}
        </Text>
      </>
    )}
  </>
);

export const LessonDetails = ({
  unit,
  lesson,
  individualLessonContent,
  downloadClassName,
  downloadTitleVariant
}: {
  unit?: SanityUnitOfWork;
  lesson: SanityLesson | SanityIndividualLesson;
  individualLessonContent?: ReactNode;
  downloadClassName: string;
  downloadTitleVariant?: TextVariant;
}) => {
  return (
    <>
      <WithSpacing>
        <div>
          <BlockContent content={lesson._rawIntroduction} />
          {individualLessonContent}
          <DownloadsSection
            unit={unit}
            lesson={lesson}
            title="Lesson plan preview"
            defaultDownloadName="Lesson plan"
            category="plan"
            className={downloadClassName}
            titleVariant={downloadTitleVariant}
          />
          <DownloadsSection
            unit={unit}
            lesson={lesson}
            title="Slides preview"
            defaultDownloadName="Slides"
            category="slides"
            className={downloadClassName}
            titleVariant={downloadTitleVariant}
          />
          <DownloadsSection
            unit={unit}
            lesson={lesson}
            title="Student handouts"
            defaultDownloadName="Student handout"
            category="student-handouts"
            embed={false}
            className={downloadClassName}
            titleVariant={downloadTitleVariant}
          />
          {lesson.completedCode && (
            <CompletedCodeSection unit={unit} lesson={lesson} />
          )}
        </div>
      </WithSpacing>
      <ContentFooter
        accessibility
        licence={unit?.licence ?? (lesson as SanityIndividualLesson).licence}
      />
      {unit && <LessonStepperBar unit={unit} lesson={lesson as SanityLesson} />}
    </>
  );
};

const LessonStepperBar = ({
  unit,
  lesson
}: {
  unit: SanityUnitOfWork;
  lesson: SanityLesson;
}) => {
  const lessonIndex = findLessonNumber(unit, lesson) - 1;
  const nextLesson = unit?.contents[lessonIndex + 1];
  const isLastLesson = lessonIndex === unit.contents.length - 1;
  const handleClick = useCallback(() => {
    navigate(urlForDocument(nextLesson));
  }, [nextLesson]);
  if (isLastLesson) {
    return null;
  }
  return (
    <StepperBarInternal
      stepNumber={lessonIndex + 2}
      buttonText={
        unit.unitType === "designChallenge" ? "Next activity" : "Next lesson"
      }
      onClick={handleClick}
    />
  );
};

export const DownloadsSection = ({
  title,
  defaultDownloadName,
  category,
  lesson,
  unit,
  className,
  embed = true,
  titleVariant = "h2"
}: {
  unit?: SanityUnitOfWork;
  lesson: SanityLesson | SanityIndividualLesson;
  title: string;
  defaultDownloadName: string;
  category: DownloadCategory;
  className: string;
  embed?: boolean;
  titleVariant?: TextVariant;
}) => {
  const downloads = lesson.downloads.filter(d => d.category === category);
  const preview = downloads.filter(d => d.pdf);
  return downloads.length === 0 ? null : (
    <>
      <Text variant={titleVariant}>{title}</Text>
      {embed &&
        preview.map((preview, i) => (
          <PdfEmbed
            key={i}
            id={preview._key}
            title={title + " file embed"}
            src={downloadUrl(preview.pdf.asset, {
              showInline: true,
              overrideFilename: preview.name ? preview.name : undefined
            })}
          />
        ))}
      <div className={className}>
        <ul>
          {downloads.map((download, i) => (
            <li key={i}>
              <DownloadLink
                to={downloadUrl(download.file.asset, {
                  overrideFilename: stripExtension(
                    lessonDownloadName(unit, lesson, download)
                  )
                })}
                fileType={download.file.asset.extension}
                text={download.name ?? defaultDownloadName}
              />
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

export const CompletedCodeSection = ({
  unit,
  lesson
}: {
  unit?: SanityUnitOfWork;
  lesson: SanityLesson | SanityIndividualLesson;
}) => {
  const code: MicrobitCode = {
    ...lesson.completedCode!,
    // Use the manually provided a key to provide the correct code links.
    // See on-create-node.ts for assignment and CodeLinks in EditorEmbed.tsx for use.
    _key: completedCodeKey
  };
  return (
    <>
      <Text variant="h2">Completed lesson code</Text>
      <Text variant="defaultLight">
        This provides an example of the code created during this lesson.
      </Text>
      <EditorEmbed unit={unit} code={code} context={lesson} />
    </>
  );
};

const stripExtension = (p: string): string => p.slice(0, p.lastIndexOf("."));
